import type {ReactNode} from 'react';
import React from 'react';
import type {Experiments, Flags} from '@sail/access';
import type {IDeferredPromise} from '../../utils/DeferredPromise';
import type {AccountSessionClaim} from '../../data-layer-frame/types';
import type {AccountSessionClaimComponentsFragmentData} from '../queries/accountSessionClaimComponentsFragment';
import type {ConnectElementEventEmitter} from '../../connect/ConnectElementEventEmitter';

type DeepOmitTypename<T extends object> = {
  [K in keyof T as Exclude<K, '__typename'>]: T[K] extends object
    ? DeepOmitTypename<T[K]>
    : T[K];
};

export type IAccountSessionComponents =
  DeepOmitTypename<AccountSessionClaimComponentsFragmentData>;

export interface AuthInfo {
  analyticsRbEventId: string;
  clientSecret: string | undefined | null;
  connectedAccountId: string;
  components: IAccountSessionComponents;
  flags: Flags;
  stripeThirdPartyBaseUrl: string;
  supportSiteBaseUrl: string | undefined;
  livemode: boolean;
  platformId: string;
  connectBrandAccentColor: string | undefined;
  connectExpressColor: string | undefined;
  platformNickname: string | undefined;
  platformBusinessName: string | undefined;
  platformCountry: string | undefined;
  platformSupportEmail: string | undefined;
  applicationId: string | undefined;
  applicationName: string | undefined;
  publishableKey: string;
  treatments: Experiments;
  readOnlyMode: boolean;
  isV2AccountSession: boolean;
  isV2SessionFromMetaOptions: boolean;
  merchantData:
    | {
        businessName: string | undefined;
        country: string;
        connectedAccountDetailsSubmitted: boolean;
        defaultCurrency: string;
        hasStandardDashboardAccess: boolean;
        hasExpressDashboardAccess: boolean;
        hasRftInLivemode?: boolean;
        stripeOwnsLossLiability: boolean;
        stripeOwnsPricing: boolean;
        stripeOwnsOnboarding: boolean;
        isControlledBySinglePlatform: boolean;
      }
    | undefined;
}

type AuthContextValue =
  | {
      status: 'initial';
      authInfo: undefined;
      isLoading: true;
      isError: false;
      isAuthContextProvided: boolean;
    }
  | {
      status: 'success';
      authInfo: AuthInfo;
      isLoading: false;
      isError: false;
      isAuthContextProvided: boolean;
    }
  | {
      status: 'error';
      authInfo: undefined;
      isLoading: false;
      isError: true;
      isAuthContextProvided: boolean;
    };

export const AuthContext = React.createContext<AuthContextValue>({
  status: 'initial',
  authInfo: undefined,
  isAuthContextProvided: false,
  isLoading: true,
  isError: false,
});
AuthContext.displayName = 'AuthContext';

export const useAuthContext = () => {
  const contextValue = React.useContext(AuthContext);
  return contextValue;
};

export type Props = {
  /**
   * Used to short circuit the authentication process. If an override is provided, it is used instead of the deferred promise
   */
  authInfoPropertiesOverride?: AccountSessionClaim;
  deferredAuthPromise: IDeferredPromise<AccountSessionClaim>;
  eventEmitter: ConnectElementEventEmitter;
  publishableKey: string;
  isMobileSdk: boolean;
  isV2SessionFromMetaOptions: boolean;
  children: (hasAuthError: boolean) => ReactNode;
  restrictToPlatformOwnsOnboarding: boolean;
};

const transformClaimResponseToAuthProperties = (
  authInfoResponse: AccountSessionClaim,
  publishableKey: string,
  isV2SessionFromMetaOptions: boolean,
): AuthInfo => ({
  analyticsRbEventId: authInfoResponse.analytics_rb_event_id,
  components: authInfoResponse.components,
  clientSecret: authInfoResponse.client_secret,
  connectedAccountId: authInfoResponse.account_id,
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  flags: authInfoResponse.flags as Flags,
  stripeThirdPartyBaseUrl: authInfoResponse.stripethirdparty_base_url,
  supportSiteBaseUrl: authInfoResponse.support_site_base_url ?? undefined,
  livemode: authInfoResponse.livemode,
  platformId: authInfoResponse.platform_id,
  connectBrandAccentColor:
    authInfoResponse.connect_brand_accent_color ?? undefined,
  connectExpressColor: authInfoResponse.connect_express_color ?? undefined,
  applicationId: authInfoResponse.application_id ?? undefined,
  applicationName: authInfoResponse.application_name ?? undefined,
  platformNickname: authInfoResponse.platform_nickname ?? undefined,
  platformBusinessName: authInfoResponse.platform_business_name ?? undefined,
  platformCountry: authInfoResponse.platform_country ?? undefined,
  platformSupportEmail: authInfoResponse.platform_support_email ?? undefined,
  publishableKey,
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  treatments: authInfoResponse.treatments as Experiments,
  readOnlyMode: authInfoResponse.read_only ?? false,
  isV2AccountSession: authInfoResponse.is_v2_account_session,
  isV2SessionFromMetaOptions,
  merchantData: authInfoResponse.merchant_data
    ? {
        businessName: authInfoResponse.merchant_data.business_name ?? undefined,
        country: authInfoResponse.merchant_data.country,
        connectedAccountDetailsSubmitted:
          authInfoResponse.merchant_data.connected_account_details_submitted,
        defaultCurrency: authInfoResponse.merchant_data.default_currency,
        hasStandardDashboardAccess:
          authInfoResponse.merchant_data.has_standard_dashboard_access,
        hasExpressDashboardAccess:
          authInfoResponse.merchant_data.has_express_dashboard_access,
        hasRftInLivemode:
          authInfoResponse.merchant_data.has_rft_in_livemode ?? undefined,
        stripeOwnsLossLiability:
          authInfoResponse.merchant_data.stripe_owns_loss_liability,
        stripeOwnsPricing: authInfoResponse.merchant_data.stripe_owns_pricing,
        stripeOwnsOnboarding:
          authInfoResponse.merchant_data.stripe_owns_onboarding,
        isControlledBySinglePlatform:
          authInfoResponse.merchant_data.is_controlled_by_single_platform,
      }
    : undefined,
});

export const AuthProvider: React.FC<Props> = ({
  children,
  authInfoPropertiesOverride,
  deferredAuthPromise,
  eventEmitter,
  publishableKey,
  isMobileSdk,
  isV2SessionFromMetaOptions,
  restrictToPlatformOwnsOnboarding,
}) => {
  const authInfoOverride = authInfoPropertiesOverride
    ? transformClaimResponseToAuthProperties(
        authInfoPropertiesOverride,
        publishableKey,
        isV2SessionFromMetaOptions,
      )
    : undefined;

  const [authInfoState, setAuthInfoState] = React.useState<AuthContextValue>(
    authInfoOverride
      ? {
          status: 'success',
          authInfo: authInfoOverride,
          isLoading: false,
          isError: false,
          isAuthContextProvided: true,
        }
      : {
          status: 'initial',
          authInfo: undefined,
          isLoading: true,
          isError: false,
          isAuthContextProvided: true,
        },
  );

  React.useEffect(() => {
    if (authInfoOverride) {
      return;
    }

    const runAsync = async () => {
      try {
        const authResult = await deferredAuthPromise.promise;
        const authInfo = transformClaimResponseToAuthProperties(
          authResult,
          publishableKey,
          isV2SessionFromMetaOptions,
        );

        // For the iOS SDK preview, we only support custom accounts where the platform owns onboarding
        if (
          restrictToPlatformOwnsOnboarding &&
          (authInfo.merchantData === undefined ||
            authInfo.merchantData.stripeOwnsOnboarding)
        ) {
          throw new Error(
            'Account must be a custom account where platform owns onboarding',
          );
        }

        // When the deferred auth promise completes, we set auth context in state/context
        setAuthInfoState((state) => ({
          ...state,
          status: 'success',
          authInfo,
          isLoading: false,
          isError: false,
        }));
      } catch (error) {
        setAuthInfoState((state) => ({
          ...state,
          status: 'error',
          authInfo: undefined,
          isLoading: false,
          isError: true,
        }));
      }
    };

    runAsync();
  }, [
    deferredAuthPromise,
    authInfoOverride,
    publishableKey,
    isV2SessionFromMetaOptions,
    restrictToPlatformOwnsOnboarding,
  ]);

  React.useEffect(() => {
    if (
      authInfoState.authInfo?.connectedAccountId &&
      isMobileSdk &&
      eventEmitter.emitEvent
    ) {
      eventEmitter.emitEvent('accountsessionclaimed', {
        merchantId: authInfoState.authInfo?.connectedAccountId,
      });
    }
  }, [authInfoState.authInfo?.connectedAccountId, eventEmitter, isMobileSdk]);

  return (
    <AuthContext.Provider value={authInfoState}>
      {children(authInfoState.status === 'error')}
    </AuthContext.Provider>
  );
};
